import { Link } from "react-router-dom";
import { Gift, Check, Sparkles, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { THERAPIST } from "@/data/catalog";
import useSEO from "@/hooks/useSEO";
import ReserveButton from "@/components/ReserveButton";

const GIFT_OPTIONS = [
    {
        id: "gc-80",
        amount: 80,
        title: "Massage Classique 60 min",
        desc: "Le cadeau détente parfait — massage suédois traditionnel",
    },
    {
        id: "gc-100",
        amount: 100,
        title: "Massage Biocorporel 60 min",
        desc: "Reconnexion corps & esprit — approche somatique intégrée",
    },
    {
        id: "gc-140",
        amount: 140,
        title: "Massage Signature 90 min",
        desc: "La création de Jérémy — techniques et musicothérapie",
        featured: true,
    },
    {
        id: "gc-180",
        amount: 180,
        title: "Massage Enrichi 2h",
        desc: "Voyage complet — massage, paysages sonores, respiration",
    },
];

export default function GiftCards() {
    useSEO({
        title: "Cartes-cadeaux · Massothérapie à Blainville — Jérémy Tessier",
        description: "Offrez un moment de bien-être. Cartes-cadeaux personnalisées valides 12 mois — massage classique, signature, biocorporel ou enrichi. À Blainville et sur la Rive-Nord.",
        canonical: "https://massoclic.ca/cartes-cadeaux",
        ogImage: "https://massoclic.ca/icon.svg",
        jsonLd: {
            "@context": "https://schema.org",
            "@type": "Product",
            name: "Carte-cadeau Massothérapie — Jérémy Tessier",
            description: "Carte-cadeau valable 12 mois pour un massage bien-être à Blainville",
            brand: { "@type": "Brand", name: "Jérémy Tessier — Massothérapie" },
            offers: GIFT_OPTIONS.map((g) => ({
                "@type": "Offer",
                price: g.amount,
                priceCurrency: "CAD",
                name: g.title,
                availability: "https://schema.org/InStock",
            })),
        },
    });

    const mailSubject = encodeURIComponent("Demande de carte-cadeau — massoclic.ca");
    const mailBody = encodeURIComponent(
        "Bonjour Jérémy,\n\nJe souhaite offrir une carte-cadeau. Voici les détails :\n\n• Montant / Soin : \n• Nom du bénéficiaire : \n• Message personnalisé (facultatif) : \n• Format souhaité : imprimée ou numérique\n\nMerci !"
    );

    return (
        <main data-testid="gift-cards-page" className="pt-32 md:pt-40 pb-20">
            <div className="max-w-6xl mx-auto px-6 md:px-12">
                {/* Hero */}
                <div className="max-w-3xl mb-14 text-center mx-auto">
                    <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-[#E8E2D9] mb-6">
                        <Gift size={14} className="text-[#E07A5F]" />
                        <span className="text-xs tracking-[0.2em] uppercase text-[#6E6862]">
                            Cartes-cadeaux
                        </span>
                    </div>
                    <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-light text-[#2C2A29] tracking-tight leading-[1.05]">
                        Offrez un moment de{" "}
                        <span className="italic text-[#E07A5F]">bien-être</span>.
                    </h1>
                    <p className="mt-6 text-base md:text-lg text-[#6E6862] leading-relaxed">
                        Anniversaire, fête des Mères, remerciement... Une carte-cadeau massothérapie personnalisée, valide 12 mois, remise en format imprimé ou numérique.
                    </p>
                    <p className="mt-4 text-sm text-[#3D8C95] italic font-medium">
                        ✓ Utilisable en clinique ou à domicile (rayon 40 km · supplément 20 $ à l&apos;unité, offert pour les forfaits) · Reçu d&apos;assurance fourni
                    </p>
                    <div className="mt-6">
                        <ReserveButton
                            mailSubject="Carte-cadeau — Achat"
                            mailBody={"Bonjour Jérémy,\n\nJe souhaite acheter une carte-cadeau.\n\n• Montant ou soin souhaité : \n• Nom du destinataire : \n• Occasion : \n• Date souhaitée de livraison : \n\nMerci !"}
                            quoteSubject="Devis — Cartes-cadeaux entreprise"
                            quoteBody={"Bonjour Jérémy,\n\nJe souhaite un devis pour l'achat groupé de cartes-cadeaux (entreprise, événement, cadeaux d'équipe).\n\n• Nombre de cartes : \n• Montant unitaire souhaité : \n• Date de remise : \n\nMerci !"}
                        />
                    </div>
                </div>

                {/* Gift options */}
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
                    {GIFT_OPTIONS.map((g) => (
                        <div
                            key={g.id}
                            data-testid={`gift-card-${g.id}`}
                            className={`relative rounded-3xl p-6 md:p-7 transition-all duration-500 hover:-translate-y-1 ${
                                g.featured
                                    ? "bg-[#E07A5F] text-white shadow-[0_25px_60px_rgba(224,122,95,0.35)]"
                                    : "bg-white border border-[#E8E2D9] shadow-[0_8px_30px_rgba(44,42,41,0.04)]"
                            }`}
                        >
                            {g.featured && (
                                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-[#3D8C95] text-white text-[10px] uppercase tracking-widest">
                                    Populaire
                                </div>
                            )}
                            <Sparkles
                                size={22}
                                className={g.featured ? "text-white/90" : "text-[#E07A5F]"}
                            />
                            <div className="mt-3">
                                <div className="font-display text-5xl md:text-6xl font-light leading-none">
                                    {g.amount}
                                </div>
                                <div className={`text-sm mt-1 ${g.featured ? "text-white/80" : "text-[#6E6862]"}`}>
                                    $ CAD
                                </div>
                            </div>
                            <h3 className={`mt-5 font-display text-lg ${g.featured ? "text-white" : "text-[#2C2A29]"}`}>
                                {g.title}
                            </h3>
                            <p className={`mt-2 text-xs leading-relaxed ${g.featured ? "text-white/85" : "text-[#6E6862]"}`}>
                                {g.desc}
                            </p>
                        </div>
                    ))}
                </div>

                {/* Features */}
                <div className="mt-14 grid md:grid-cols-3 gap-6">
                    {[
                        { icon: Check, title: "Valide 12 mois", desc: "Le bénéficiaire choisit sa date." },
                        { icon: Gift, title: "Format au choix", desc: "Numérique par courriel ou imprimée en cabinet." },
                        { icon: Sparkles, title: "Message personnalisé", desc: "Ajoutez un mot au bénéficiaire." },
                    ].map((f, i) => (
                        <div
                            key={i}
                            className="rounded-2xl p-5 bg-white border border-[#E8E2D9] flex items-start gap-4"
                        >
                            <div className="w-11 h-11 rounded-2xl bg-[#3D8C95]/10 flex items-center justify-center flex-shrink-0">
                                <f.icon size={20} className="text-[#3D8C95]" />
                            </div>
                            <div>
                                <h4 className="font-display text-base text-[#2C2A29]">{f.title}</h4>
                                <p className="text-xs text-[#6E6862] mt-1">{f.desc}</p>
                            </div>
                        </div>
                    ))}
                </div>

                {/* CTA */}
                <div className="mt-16 rounded-3xl bg-[#2C2A29] text-white p-8 md:p-12 text-center">
                    <h2 className="font-display text-3xl md:text-4xl leading-tight">
                        Prêt à offrir une pause ?
                    </h2>
                    <p className="mt-3 text-white/75 max-w-2xl mx-auto leading-relaxed">
                        Envoyez-moi un courriel avec le montant, le nom du bénéficiaire et votre message.
                        Je vous prépare la carte-cadeau dans les 24 h.
                    </p>
                    <div className="mt-8 flex flex-wrap justify-center gap-3">
                        <a
                            href={`mailto:${THERAPIST.email}?subject=${mailSubject}&body=${mailBody}`}
                            data-testid="gift-mail-cta"
                        >
                            <Button className="rounded-full bg-[#E07A5F] hover:bg-[#D06A4F] text-white px-8 py-6 text-base">
                                <Mail size={16} className="mr-2" /> Commander par courriel
                            </Button>
                        </a>
                        <Link to="/#reservation">
                            <Button
                                variant="outline"
                                className="rounded-full border-white text-white hover:bg-white hover:text-[#2C2A29] px-8 py-6 text-base"
                            >
                                Voir les autres soins
                            </Button>
                        </Link>
                    </div>
                </div>
            </div>
        </main>
    );
}
